import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase, isConfigured } from '../lib/supabase';
import { PUBLIC_AUDIENCES } from '../nav';
import { feedEnabled } from '../lib/docsFeed';

// 미리보기(단일 파일) 빌드는 아티팩트 CSP 가 외부 fetch 를 막아 실제 로그인이
// 불가능하므로, 역할을 골라 게이트 동작을 확인하는 모드로 대체한다.
export const IS_PREVIEW = process.env.REACT_APP_PREVIEW === '1';

const AuthContext = createContext(null);

// 본 앱 ProtectedRoute.jsx 의 role 문자열 규칙을 따른다.
// Admin/Master/Manager 는 전체 열람(내부 직원용 staff·internal 포함), Copyrights Holder 는 권리사,
// 그 외(Creator 포함)는 크리에이터로 본다. 크리에이터 겸 영상권리사 겸직은 두 대상 가이드만 — 내부용은 못 본다(2026-10-15).
// 서버 docs-feed scopeFromRole · lay-docs-chat scopeFromRole 과 같은 규칙. 한쪽을 바꾸면 나머지도 바꾼다.
export function audiencesFromRole(role) {
  const r = role || '';
  const set = new Set();
  if (r.includes('Admin') || r.includes('Master') || r.includes('Manager')) {
    set.add('creator'); set.add('rights'); set.add('staff'); set.add('internal');
    return set;
  }
  // 파트너(비어드민)는 겸직이어도 파트너 문서만 (2026-09-10, 기획 partner-program-plan.md §2)
  if (r.includes('Partner')) { set.add('partner'); return set; }
  if (r.includes('Copyrights Holder')) set.add('rights');
  if (r.includes('Creator')) set.add('creator');
  if (set.size === 0) set.add('creator');
  return set;
}

const PREVIEW_ROLES = {
  creator: { email: 'preview-creator@laeebly.io', role: 'Creator', label: '크리에이터' },
  rights: { email: 'preview-rights@laeebly.io', role: 'Copyrights Holder', label: '영상권리사' },
  admin: { email: 'preview-admin@laeebly.io', role: 'Admin', label: '관리자' },
};

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading'); // loading | signedOut | ready
  const [profile, setProfile] = useState(null);    // { email, role, audiences, roleError?, previewLabel? }

  const applyUser = useCallback(async (email) => {
    // 피드 모드에서는 역할 판정을 서버(docs-feed)가 한다 — 여기서 "user" 표를 읽지 않는다.
    // (DocsContext 가 서버 scope 로 audiences 를 만든다. 이 값은 정적 모드에서만 쓰인다.)
    if (feedEnabled) {
      setProfile({ email, role: null, audiences: PUBLIC_AUDIENCES });
      setStatus('ready');
      return;
    }
    let role = '';
    let roleError = false;
    try {
      const { data, error } = await supabase.from('user').select('role').eq('email', email).maybeSingle();
      if (error) throw error;
      role = data?.role || '';
    } catch (e) {
      roleError = true; // 조회 실패는 공개 문서까지만(fail-closed). 크리에이터로 넓히지 않는다
    }
    setProfile({ email, role, roleError, audiences: roleError ? PUBLIC_AUDIENCES : audiencesFromRole(role) });
    setStatus('ready');
  }, []);

  useEffect(() => {
    if (IS_PREVIEW) {
      let saved = null;
      try { saved = sessionStorage.getItem('docs-preview-role'); } catch (e) { saved = null; }
      if (saved && PREVIEW_ROLES[saved]) {
        const p = PREVIEW_ROLES[saved];
        setProfile({ email: p.email, role: p.role, audiences: audiencesFromRole(p.role), previewLabel: p.label });
        setStatus('ready');
      } else {
        setStatus('signedOut');
      }
      return undefined;
    }

    if (!isConfigured) { setStatus('signedOut'); return undefined; }

    supabase.auth.getSession().then(({ data }) => {
      const email = data?.session?.user?.email;
      if (email) applyUser(email); else setStatus('signedOut');
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const email = session?.user?.email;
      if (email) applyUser(email);
      else { setProfile(null); setStatus('signedOut'); }
    });
    return () => sub?.subscription?.unsubscribe();
  }, [applyUser]);

  const signIn = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (/invalid login credentials/i.test(error.message)) {
        throw new Error('이메일 또는 비밀번호가 맞지 않습니다.');
      }
      throw new Error('로그인하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    }
  }, []);

  const signOut = useCallback(async () => {
    if (IS_PREVIEW) {
      try { sessionStorage.removeItem('docs-preview-role'); } catch (e) { /* noop */ }
      setProfile(null); setStatus('signedOut');
      return;
    }
    await supabase.auth.signOut();
  }, []);

  const setPreviewRole = useCallback((key) => {
    const p = PREVIEW_ROLES[key];
    if (!p) return;
    try { sessionStorage.setItem('docs-preview-role', key); } catch (e) { /* noop */ }
    setProfile({ email: p.email, role: p.role, audiences: audiencesFromRole(p.role), previewLabel: p.label });
    setStatus('ready');
  }, []);

  // 로그인 전에는 공개 문서만 열람 범위에 넣는다. 화면들은 profile 유무가 아니라
  // 이 audiences 를 보고 목록·검색·본문 접근을 정한다.
  const audiences = profile?.audiences || PUBLIC_AUDIENCES;
  // 문서 캐시 키 — 사용자가 바뀌면(로그인·로그아웃) DocsContext 가 목록·본문을 다시 받는다.
  const authKey = status === 'ready' && profile?.email ? profile.email : 'anon';

  const value = useMemo(
    () => ({
      status, profile, audiences, authKey, signedIn: status === 'ready', roleError: Boolean(profile?.roleError),
      signIn, signOut, setPreviewRole, isPreview: IS_PREVIEW, isConfigured,
    }),
    [status, profile, audiences, authKey, signIn, signOut, setPreviewRole]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
